'use client'

import { useCallback } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import type { ContactOrUser, ShareRoomGuestInviteRow, ShareContentRoomMemberForSubmit } from '@/components/share-room/types'

type ToastFn = (args: {
  title: string
  description?: string
  variant?: 'default' | 'destructive'
}) => void

export type UseShareRoomInviteSubmitParams = {
  roomId: string
  accountId: string
  contentId?: string
  contentName?: string
  message: string
  createShareableLink: boolean
  selectedContactsAndUsers: ContactOrUser[]
  guestInviteRows: ShareRoomGuestInviteRow[]
  sessionUserType: string | undefined
  isCommunityRoomContext: boolean
  communityBulkNoAccount: boolean
  communityBulkAccountId: string | null
  resolveExternalInviteContext: () => {
    inviteAccountId: string | null
    inviteContactsWithoutAccount: boolean
  }
  setIsSendingInvites: (value: boolean) => void
  setConfirmationMessage: (value: string) => void
  setIsShareModalOpen: (value: boolean) => void
  setShowConfirmation: (value: boolean) => void
  setShowConfetti: (value: boolean) => void
  setGuestInviteRows: Dispatch<SetStateAction<ShareRoomGuestInviteRow[]>>
  handleCloseConfirmation: () => void
  toast: ToastFn
  onMembersUpdated?: () => void
}

export function useShareRoomInviteSubmit(params: UseShareRoomInviteSubmitParams) {
  const {
    roomId,
    accountId,
    contentId,
    contentName,
    message,
    createShareableLink,
    selectedContactsAndUsers,
    guestInviteRows,
    sessionUserType,
    isCommunityRoomContext,
    communityBulkNoAccount,
    communityBulkAccountId,
    resolveExternalInviteContext,
    setIsSendingInvites,
    setConfirmationMessage,
    setIsShareModalOpen,
    setShowConfirmation,
    setShowConfetti,
    setGuestInviteRows,
    handleCloseConfirmation,
    toast,
    onMembersUpdated,
  } = params

  const handleShare = useCallback(async () => {
    const emailInviteExtras = guestInviteRows.map((r) => ({
      type: 'email_invite' as const,
      email: r.email,
      firstName: r.firstName || undefined,
      lastName: r.lastName || undefined,
    }))

    if (
      sessionUserType === 'INTERNAL' &&
      isCommunityRoomContext &&
      emailInviteExtras.length > 0 &&
      !communityBulkNoAccount &&
      !communityBulkAccountId
    ) {
      toast({
        title: 'Select account context',
        description:
          'Choose which account these contacts belong to, or mark “Contacts without account”.',
        variant: 'destructive',
      })
      return
    }

    const { inviteAccountId, inviteContactsWithoutAccount } = (() => {
      if (sessionUserType === 'EXTERNAL') {
        return resolveExternalInviteContext()
      }
      if (isCommunityRoomContext) {
        return {
          inviteAccountId: communityBulkNoAccount ? null : communityBulkAccountId,
          inviteContactsWithoutAccount: communityBulkNoAccount,
        }
      }
      return {
        inviteAccountId: accountId || null,
        inviteContactsWithoutAccount: false,
      }
    })()

    try {
      setIsSendingInvites(true)
      const response = await fetch('/api/share-room', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          roomId,
          selectedUsers: [...selectedContactsAndUsers, ...emailInviteExtras],
          message,
          createShareableLink,
          contentId: contentId,
          contentName: contentName,
          inviteAccountId,
          inviteContactsWithoutAccount,
        }),
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({}))
        throw new Error((err as { error?: string }).error || 'Failed to share room')
      }

      const result = await response.json()

      let successMsg = result.message || (contentName ? 'Content shared successfully.' : 'Room shared successfully.')

      if (!contentName && result.alreadyAddedUsers && result.alreadyAddedUsers.length > 0) {
        const alreadyAddedMessage = `${result.alreadyAddedUsers.join(', ')} ${result.alreadyAddedUsers.length === 1 ? 'was' : 'were'} already added to the room.`
        successMsg += ` ${alreadyAddedMessage}`
      }

      setConfirmationMessage(successMsg)
      setIsShareModalOpen(false)
      setShowConfirmation(true)
      setShowConfetti(true)

      setTimeout(() => {
        handleCloseConfirmation()
      }, 5000)

      onMembersUpdated?.()
      setGuestInviteRows([])
    } catch (error) {
      console.error('Error sharing room:', error)
      toast({
        title: 'Error sharing room',
        description:
          error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsSendingInvites(false)
    }
  }, [
    roomId,
    selectedContactsAndUsers,
    guestInviteRows,
    message,
    createShareableLink,
    toast,
    handleCloseConfirmation,
    contentId,
    contentName,
    onMembersUpdated,
    sessionUserType,
    isCommunityRoomContext,
    communityBulkNoAccount,
    communityBulkAccountId,
    accountId,
    resolveExternalInviteContext,
    setIsSendingInvites,
    setConfirmationMessage,
    setIsShareModalOpen,
    setShowConfirmation,
    setShowConfetti,
    setGuestInviteRows,
  ])

  const handleCommunityGuestShare = useCallback(
    async (existingRoomMembers: ShareContentRoomMemberForSubmit[] = []) => {
      if (guestInviteRows.length === 0 && existingRoomMembers.length === 0) {
        toast({
          title: contentName ? 'Choose who to share with' : 'Add at least one email',
          description: contentName
            ? 'Select people already in the room and/or add someone new by email.'
            : 'Or copy the link below to share the room outside the app.',
          variant: 'destructive',
        })
        return
      }
      try {
        setIsSendingInvites(true)
        const { inviteAccountId, inviteContactsWithoutAccount } = resolveExternalInviteContext()
        const selectedEmails = new Set(
          existingRoomMembers.map((m) => m.email.trim().toLowerCase()).filter(Boolean)
        )
        const guestRowsDeduped = guestInviteRows.filter(
          (r) => !selectedEmails.has(r.email.trim().toLowerCase())
        )
        const existingAsUsers = existingRoomMembers.map((m) => ({
          type: 'user' as const,
          id: m.id,
          email: m.email,
          firstName: m.firstName,
          lastName: m.lastName,
        }))
        if (existingAsUsers.length === 0 && guestRowsDeduped.length === 0) {
          toast({
            title: 'Duplicate recipients',
            description:
              'Everyone you added by email is already included via the room list. Remove duplicates or pick someone else.',
            variant: 'destructive',
          })
          return
        }
        const response = await fetch('/api/share-room', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            roomId,
            selectedUsers: [
              ...existingAsUsers,
              ...guestRowsDeduped.map((r) => ({
                type: 'email_invite',
                email: r.email,
                firstName: r.firstName || undefined,
                lastName: r.lastName || undefined,
              })),
            ],
            message,
            createShareableLink: false,
            contentId,
            contentName,
            inviteAccountId,
            inviteContactsWithoutAccount,
          }),
        })

        if (!response.ok) {
          const err = await response.json().catch(() => ({}))
          throw new Error((err as { error?: string }).error || 'Failed to send invitations')
        }

        const result = await response.json()

        let successMsg =
          result.message || (contentName ? 'Shared successfully.' : 'Invitations sent.')

        if (!contentName && result.alreadyAddedUsers && result.alreadyAddedUsers.length > 0) {
          const alreadyAddedMessage = `${result.alreadyAddedUsers.join(', ')} ${result.alreadyAddedUsers.length === 1 ? 'was' : 'were'} already in the room.`
          successMsg += ` ${alreadyAddedMessage}`
        }

        setConfirmationMessage(successMsg)
        setIsShareModalOpen(false)
        setShowConfirmation(true)
        setShowConfetti(true)

        setTimeout(() => {
          handleCloseConfirmation()
        }, 5000)

        onMembersUpdated?.()
        setGuestInviteRows([])
      } catch (error) {
        console.error('Error sharing room:', error)
        toast({
          title: 'Could not send invitations',
          description:
            error instanceof Error ? error.message : 'An unexpected error occurred.',
          variant: 'destructive',
        })
      } finally {
        setIsSendingInvites(false)
      }
    },
    [
      guestInviteRows,
      roomId,
      message,
      contentId,
      contentName,
      toast,
      handleCloseConfirmation,
      onMembersUpdated,
      resolveExternalInviteContext,
      setIsSendingInvites,
      setConfirmationMessage,
      setIsShareModalOpen,
      setShowConfirmation,
      setShowConfetti,
      setGuestInviteRows,
    ]
  )

  return { handleShare, handleCommunityGuestShare }
}
